import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import LockResetIcon from '@mui/icons-material/LockReset';
import PaidIcon from '@mui/icons-material/Paid';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormPage } from '../../shared/components/FormPage';
import { useCrudFormDialog } from '../../shared/api/useCrudFormDialog';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { stripDocumentoMask } from '../../shared/utils/document';
import { api } from '../../shared/api/client';
import { employeesHooks, useEmployeeLoginHooks } from './hooks';
import { employeeInputSchema, type EmployeeFormValues } from './schemas';
import type { EmployeeDto, EmployeeInput } from './types';
import { EmployeePasswordDialog } from './EmployeePasswordDialog';
import { EmployeeTaxasDialog } from './taxas/EmployeeTaxasDialog';
import { EmployeeFaltasDialog } from './faltas/EmployeeFaltasDialog';
import { EmployeeFeriasDialog } from './ferias/EmployeeFeriasDialog';
import { EmployeeValesDialog } from './vales/EmployeeValesDialog';
import { EmployeeNotasFiscaisDialog } from './notas-fiscais/EmployeeNotasFiscaisDialog';

export interface EmployeeFormDialogProps {
  open: boolean;
  employee: EmployeeDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeeFormValues = {
  nome: '',
  cpf: '',
  sexo: 'M',
  tipoContrato: 'CLT',
  empresaNome: null,
  empresaCnpj: null,
  email: '',
  telefone: null,
  receberEmail: false,
  login: null,
  tipoPagamento: 'H',
  // 5 campos required no Input upstream mas ignorados na persistência (memória `colaborador-status-via-data-demissao` § quirks).
  valorHoraPagar: '0',
  valorHoraReceber: '0',
  valorFixoPagar: '0',
  valorFixoReceber: '0',
  cargoId: null,
  endereco: '',
  dataAdmissao: new Date().toISOString().slice(0, 10),
  dataDemissao: null,
};

interface PositionOption {
  id: number;
  descricao: string;
}

export function EmployeeFormDialog({
  open,
  employee,
  onClose,
}: EmployeeFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeFormValues,
    EmployeeDto,
    EmployeeInput,
    number
  >({
    open,
    entity: employee,
    schema: employeeInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (e) => ({
      nome: e.nome,
      cpf: e.cpf,
      sexo: e.sexo,
      tipoContrato: e.tipoContrato,
      empresaNome: e.empresaNome,
      empresaCnpj: e.empresaCnpj,
      email: e.email,
      telefone: e.telefone,
      receberEmail: e.receberEmail,
      login: e.login,
      tipoPagamento: e.tipoPagamento ?? 'H',
      valorHoraPagar: String(e.valorHoraPagar ?? ''),
      valorHoraReceber: String(e.valorHoraReceber ?? ''),
      valorFixoPagar: String(e.valorFixoPagar ?? ''),
      valorFixoReceber: String(e.valorFixoReceber ?? ''),
      cargoId: e.cargoId,
      endereco: e.endereco ?? '',
      dataAdmissao: e.dataAdmissao,
      dataDemissao: e.dataDemissao,
    }),
    toInput: (v) => ({
      ...v,
      cpf: stripDocumentoMask(v.cpf),
      empresaCnpj: v.empresaCnpj ? stripDocumentoMask(v.empresaCnpj) : null,
      valorHoraPagar: v.valorHoraPagar ?? '0',
      valorHoraReceber: v.valorHoraReceber ?? '0',
      valorFixoPagar: v.valorFixoPagar ?? '0',
      valorFixoReceber: v.valorFixoReceber ?? '0',
    }),
    getId: (e) => e.id,
    hooks: { useCreate: employeesHooks.useCreate, useUpdate: employeesHooks.useUpdate },
    messages: {
      created: t('features.colaboradores.toasts.created'),
      updated: t('features.colaboradores.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  const tipoContrato = useWatch({ control: form.control, name: 'tipoContrato' });

  // Autocomplete de Cargo — async, lazy on-open.
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionInput, setPositionInput] = useState('');
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      setPositionsLoading(true);
      try {
        const { data } = await api.get<{ data: PositionOption[] }>('/v1/positions', {
          params: { pagina: 1, porPagina: 50, busca: positionInput || undefined },
        });
        if (!cancelled) setPositions(data.data);
      } catch {
        if (!cancelled) setPositions([]);
      } finally {
        if (!cancelled) setPositionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, positionInput]);

  // Ações de login
  const { useActivate, useBlock, useUnblock } = useEmployeeLoginHooks();
  const activateMutation = useActivate();
  const blockMutation = useBlock();
  const unblockMutation = useUnblock();
  const [confirmAction, setConfirmAction] = useState<'activate' | 'block' | 'unblock' | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [taxasOpen, setTaxasOpen] = useState(false);
  const [faltasOpen, setFaltasOpen] = useState(false);
  const [feriasOpen, setFeriasOpen] = useState(false);
  const [valesOpen, setValesOpen] = useState(false);
  const [notasFiscaisOpen, setNotasFiscaisOpen] = useState(false);

  const loginEditable = !employee || employee.statusLogin === 'DES';
  const canActivate = employee?.statusLogin === 'DES' && Boolean(employee.login);
  const canBlock = employee?.statusLogin === 'ATV';
  const canUnblock = employee?.statusLogin === 'BLQ';
  const canSetPassword = employee?.statusLogin === 'ATV' || employee?.statusLogin === 'BLQ';

  async function runLoginAction(action: 'activate' | 'block' | 'unblock'): Promise<void> {
    if (!employee) return;
    try {
      const fn =
        action === 'activate'
          ? activateMutation.mutateAsync
          : action === 'block'
            ? blockMutation.mutateAsync
            : unblockMutation.mutateAsync;
      await fn(employee.id);
      toast.success(
        t(
          action === 'activate'
            ? 'features.colaboradores.toasts.loginActivated'
            : action === 'block'
              ? 'features.colaboradores.toasts.loginBlocked'
              : 'features.colaboradores.toasts.loginUnblocked',
        ),
      );
      setConfirmAction(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <>
      <FormPage
        title={
          isEdit ? t('features.colaboradores.editTitle') : t('features.colaboradores.createTitle')
        }
        form={form}
        onSubmit={onSubmit}
        onCancel={onClose}
        serverError={serverError}
      >
        <Stack spacing={2}>
          {/* ─── Dados básicos ─────────────────────────────────────── */}
          <TextField
            label={t('features.colaboradores.fields.nome')}
            fullWidth
            inputProps={{ maxLength: 150 }}
            error={Boolean(form.formState.errors.nome)}
            helperText={form.formState.errors.nome?.message}
            {...form.register('nome')}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('features.colaboradores.fields.cpf')}
              fullWidth
              inputProps={{ maxLength: 14 }}
              error={Boolean(form.formState.errors.cpf)}
              helperText={
                form.formState.errors.cpf?.message ?? t('features.colaboradores.fields.cpfHelper')
              }
              {...form.register('cpf')}
            />
            <Controller
              control={form.control}
              name="sexo"
              render={({ field }) => (
                <TextField
                  select
                  label={t('features.colaboradores.fields.sexo')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="F">{t('features.colaboradores.sexo.F')}</MenuItem>
                  <MenuItem value="M">{t('features.colaboradores.sexo.M')}</MenuItem>
                </TextField>
              )}
            />
          </Stack>

          <Controller
            control={form.control}
            name="tipoContrato"
            render={({ field }) => (
              <FormControl>
                <FormLabel>{t('features.colaboradores.fields.tipoContrato')}</FormLabel>
                <RadioGroup
                  row
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  onBlur={field.onBlur}
                >
                  <FormControlLabel
                    value="CLT"
                    control={<Radio />}
                    label={t('features.colaboradores.tipoContrato.CLT')}
                  />
                  <FormControlLabel
                    value="PJ"
                    control={<Radio />}
                    label={t('features.colaboradores.tipoContrato.PJ')}
                  />
                  <FormControlLabel
                    value="TMP"
                    control={<Radio />}
                    label={t('features.colaboradores.tipoContrato.TMP')}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {tipoContrato === 'PJ' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('features.colaboradores.fields.empresaNome')}
                fullWidth
                inputProps={{ maxLength: 150 }}
                error={Boolean(form.formState.errors.empresaNome)}
                helperText={form.formState.errors.empresaNome?.message}
                {...form.register('empresaNome')}
              />
              <TextField
                label={t('features.colaboradores.fields.empresaCnpj')}
                fullWidth
                inputProps={{ maxLength: 18 }}
                error={Boolean(form.formState.errors.empresaCnpj)}
                helperText={
                  form.formState.errors.empresaCnpj?.message ??
                  t('features.colaboradores.fields.empresaCnpjHelper')
                }
                {...form.register('empresaCnpj')}
              />
            </Stack>
          )}

          <Divider textAlign="left" sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('features.colaboradores.sections.vinculo')}
            </Typography>
          </Divider>

          <Controller
            control={form.control}
            name="cargoId"
            render={({ field, fieldState }) => (
              <Autocomplete<PositionOption, false, false, false>
                options={positions}
                value={positions.find((p) => p.id === field.value) ?? null}
                onChange={(_, v) => field.onChange(v?.id ?? null)}
                onInputChange={(_, v) => setPositionInput(v)}
                loading={positionsLoading}
                getOptionLabel={(o) => o.descricao}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('features.colaboradores.fields.cargoId')}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('features.colaboradores.fields.email')}
              fullWidth
              inputProps={{ maxLength: 150 }}
              error={Boolean(form.formState.errors.email)}
              helperText={form.formState.errors.email?.message}
              {...form.register('email')}
            />
            <TextField
              label={t('features.colaboradores.fields.telefone')}
              fullWidth
              inputProps={{ maxLength: 20 }}
              error={Boolean(form.formState.errors.telefone)}
              helperText={form.formState.errors.telefone?.message}
              {...form.register('telefone')}
            />
          </Stack>

          <Controller
            control={form.control}
            name="receberEmail"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(_, c) => field.onChange(c)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                }
                label={t('features.colaboradores.fields.receberEmail')}
              />
            )}
          />

          <TextField
            label={t('features.colaboradores.fields.endereco')}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            error={Boolean(form.formState.errors.endereco)}
            helperText={form.formState.errors.endereco?.message}
            {...form.register('endereco')}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date"
              label={t('features.colaboradores.fields.dataAdmissao')}
              InputLabelProps={{ shrink: true }}
              error={Boolean(form.formState.errors.dataAdmissao)}
              helperText={form.formState.errors.dataAdmissao?.message}
              {...form.register('dataAdmissao')}
            />
            {isEdit && (
              <Controller
                control={form.control}
                name="dataDemissao"
                render={({ field, fieldState }) => (
                  <TextField
                    type="date"
                    label={t('features.colaboradores.fields.dataDemissao')}
                    InputLabelProps={{ shrink: true }}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      t('features.colaboradores.fields.dataDemissaoHelper')
                    }
                  />
                )}
              />
            )}
          </Stack>

          {/* ─── Taxas (sub-recurso /employees/{id}/taxas) ─────────── */}
          {isEdit && employee && (
            <>
              <Divider textAlign="left" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('features.colaboradores.taxas.sectionTitle')}
                </Typography>
              </Divider>
              <Stack direction="row" justifyContent="flex-start" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PaidIcon />}
                  onClick={() => setTaxasOpen(true)}
                >
                  {t('features.colaboradores.taxas.actions.manage')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EventBusyIcon />}
                  onClick={() => setFaltasOpen(true)}
                >
                  {t('features.colaboradores.faltas.actions.manage')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BeachAccessIcon />}
                  onClick={() => setFeriasOpen(true)}
                >
                  {t('features.colaboradores.ferias.actions.manage')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RequestQuoteIcon />}
                  onClick={() => setValesOpen(true)}
                >
                  {t('features.colaboradores.vales.actions.manage')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => setNotasFiscaisOpen(true)}
                >
                  {t('features.colaboradores.notasFiscais.actions.manage')}
                </Button>
              </Stack>
            </>
          )}

          {/* ─── Acesso ao sistema (só em edit) ─────────────────────── */}
          {isEdit && employee && (
            <>
              <Divider textAlign="left" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('features.colaboradores.sections.acesso')}
                </Typography>
              </Divider>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('features.colaboradores.fields.statusLogin' as never, {
                    defaultValue: 'Status:',
                  })}
                </Typography>
                <Chip
                  label={t(`features.colaboradores.statusLogin.${employee.statusLogin}`)}
                  size="small"
                  color={
                    employee.statusLogin === 'ATV'
                      ? 'success'
                      : employee.statusLogin === 'BLQ'
                        ? 'error'
                        : 'default'
                  }
                  variant={employee.statusLogin === 'DES' ? 'outlined' : 'filled'}
                />
              </Stack>

              <TextField
                label={t('features.colaboradores.fields.login')}
                fullWidth
                inputProps={{ maxLength: 60 }}
                disabled={!loginEditable}
                error={Boolean(form.formState.errors.login)}
                helperText={
                  form.formState.errors.login?.message ??
                  (loginEditable
                    ? t('features.colaboradores.fields.loginHelperEditable')
                    : t('features.colaboradores.fields.loginHelperLocked'))
                }
                {...form.register('login')}
              />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<KeyIcon />}
                  disabled={!canActivate || activateMutation.isPending}
                  onClick={() => setConfirmAction('activate')}
                >
                  {t('features.colaboradores.actions.loginActivate')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<LockIcon />}
                  disabled={!canBlock || blockMutation.isPending}
                  onClick={() => setConfirmAction('block')}
                >
                  {t('features.colaboradores.actions.loginBlock')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  disabled={!canUnblock || unblockMutation.isPending}
                  onClick={() => setConfirmAction('unblock')}
                >
                  {t('features.colaboradores.actions.loginUnblock')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LockResetIcon />}
                  disabled={!canSetPassword}
                  onClick={() => setPasswordOpen(true)}
                >
                  {t('features.colaboradores.actions.loginPassword')}
                </Button>
              </Stack>

              {employee.statusLogin === 'DES' && !employee.login && (
                <Alert severity="info" variant="outlined">
                  {t('features.colaboradores.fields.loginHelperEditable')}
                </Alert>
              )}
            </>
          )}
        </Stack>
      </FormPage>

      {/* Confirm dialogs das ações de login */}
      <ConfirmDialog
        open={confirmAction === 'activate'}
        title={t('features.colaboradores.loginConfirm.activateTitle')}
        description={t('features.colaboradores.loginConfirm.activateDescription', {
          nome: employee?.nome ?? '',
        })}
        confirmLabel={t('features.colaboradores.actions.loginActivate')}
        loading={activateMutation.isPending}
        destructive={false}
        onConfirm={() => runLoginAction('activate')}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'block'}
        title={t('features.colaboradores.loginConfirm.blockTitle')}
        description={t('features.colaboradores.loginConfirm.blockDescription', {
          nome: employee?.nome ?? '',
        })}
        confirmLabel={t('features.colaboradores.actions.loginBlock')}
        loading={blockMutation.isPending}
        onConfirm={() => runLoginAction('block')}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'unblock'}
        title={t('features.colaboradores.loginConfirm.unblockTitle')}
        description={t('features.colaboradores.loginConfirm.unblockDescription', {
          nome: employee?.nome ?? '',
        })}
        confirmLabel={t('features.colaboradores.actions.loginUnblock')}
        loading={unblockMutation.isPending}
        destructive={false}
        onConfirm={() => runLoginAction('unblock')}
        onCancel={() => setConfirmAction(null)}
      />

      <EmployeePasswordDialog
        open={passwordOpen}
        employee={employee}
        onClose={() => setPasswordOpen(false)}
      />

      <EmployeeTaxasDialog
        open={taxasOpen}
        employeeId={employee?.id ?? null}
        employeeName={employee?.nome}
        onClose={() => setTaxasOpen(false)}
      />

      <EmployeeFaltasDialog
        open={faltasOpen}
        employeeId={employee?.id ?? null}
        employeeName={employee?.nome}
        onClose={() => setFaltasOpen(false)}
      />

      <EmployeeValesDialog
        open={valesOpen}
        employeeId={employee?.id ?? null}
        employeeName={employee?.nome}
        onClose={() => setValesOpen(false)}
      />

      <EmployeeFeriasDialog
        open={feriasOpen}
        employeeId={employee?.id ?? null}
        employeeName={employee?.nome}
        onClose={() => setFeriasOpen(false)}
      />

      <EmployeeNotasFiscaisDialog
        open={notasFiscaisOpen}
        employeeId={employee?.id ?? null}
        employeeName={employee?.nome}
        onClose={() => setNotasFiscaisOpen(false)}
      />
    </>
  );
}
